import React, { useState } from 'react';
import { savePendingReferral, getPendingReferral } from '../utils/referral';
import GoogleSignInButton, { OrDivider } from '../components/auth/GoogleSignInButton';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Lock, User, ArrowRight, Heart } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, login, isAuthenticated } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessType, setBusinessType] = useState('sole_proprietor');
  const [filingStatus, setFilingStatus] = useState('single');
  const [loading, setLoading] = useState(false);
  // D1: ToS acceptance required at registration (GDPR / legal compliance)
  const [tosAccepted, setTosAccepted] = useState(false);

  const [justRegistered, setJustRegistered] = React.useState(false);
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState(null);
  React.useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) savePendingReferral(ref);
    setReferralCode(getPendingReferral());
  }, [searchParams]);

  React.useEffect(() => {
    // Don't redirect to dashboard if user just registered - they need to go to pricing
    if (isAuthenticated && !justRegistered) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate, justRegistered]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // D1: Block submission if ToS not accepted
    if (!tosAccepted) {
      toast.error(t('register.tos_required', 'You must accept the Terms of Service to create an account.'));
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name, businessType, filingStatus, { tos_accepted: true });
      toast.success(t('register.success'));
      setJustRegistered(true);
      await login(email, password);
      navigate('/pricing');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('register.failed'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    t('register.benefit_1'),
    t('register.benefit_2'),
    t('register.benefit_3'),
    t('register.benefit_4'),
    t('register.benefit_5'),
    t('register.benefit_6')
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form (White Background) */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8" data-testid="register-logo">
            <div className="w-10 h-10 relative flex items-center justify-center">
              <div className="absolute inset-0 bg-emerald-600 rounded-xl transform rotate-45"></div>
              <div className="relative w-4 h-4 bg-white z-10"></div>
            </div>
            <span className="text-xl font-semibold">
              <span className="text-gray-900">Solely</span>
              <span className="text-emerald-600">Tax</span>
            </span>
          </Link>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">{t('register.title')}</h1>
              <p className="text-gray-500 mt-1">{t('register.subtitle')}</p>
            </div>

            {referralCode && (
              <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="register-referral-banner">
                <span className="font-semibold">Referral code {referralCode} will be applied when you sign up.</span> You and your friend each get a free month after your first payment.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-medium">{t('register.full_name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">{t('register.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">{t('register.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
                    required
                    minLength={8}
                    data-testid="register-password"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  {t('register.password_requirements')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessType" className="text-gray-700 font-medium">{t('register.business_type')}</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger className="bg-white border-gray-200 text-gray-900" data-testid="register-business-type">
                    <SelectValue placeholder={t('register.select_business_type')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sole_proprietor">{t('register.sole_proprietor')}</SelectItem>
                    <SelectItem value="llc">{t('register.llc')}</SelectItem>
                    <SelectItem value="s_corp">{t('register.s_corp')}</SelectItem>
                    <SelectItem value="c_corp">{t('register.c_corp')}</SelectItem>
                    <SelectItem value="partnership">{t('register.partnership')}</SelectItem>
                    <SelectItem value="startup">{t('register.startup')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="filingStatus" className="text-gray-700 font-medium flex items-center gap-2">
                  <Heart className="w-4 h-4 text-pink-500" />
                  {t('register.filing_status')}
                </Label>
                <Select value={filingStatus} onValueChange={setFilingStatus}>
                  <SelectTrigger className="bg-white border-gray-200 text-gray-900" data-testid="register-filing-status">
                    <SelectValue placeholder={t('profile.select_filing_status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">{t('register.single')}</SelectItem>
                    <SelectItem value="married_filing_jointly">{t('register.married_joint')}</SelectItem>
                    <SelectItem value="married_filing_separately">{t('register.married_separate')}</SelectItem>
                    <SelectItem value="head_of_household">{t('register.head_household')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {filingStatus === 'married_filing_jointly' && t('register.spouse_note')}
                </p>
              </div>

              {/* D1: ToS acceptance — required for GDPR / legal compliance */}
              <div className="flex items-start gap-3 py-1">
                <input
                  type="checkbox"
                  id="tos-accepted"
                  checked={tosAccepted}
                  onChange={e => setTosAccepted(e.target.checked)}
                  data-testid="tos-checkbox"
                  className="mt-0.5 w-4 h-4 accent-emerald-700 cursor-pointer"
                />
                <label htmlFor="tos-accepted" className="text-sm text-gray-600 cursor-pointer">
                  {t('register.tos_label', 'I agree to the')}{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline font-medium">
                    {t('register.terms_of_service', 'Terms of Service')}
                  </a>{' '}
                  {t('register.and', 'and')}{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline font-medium">
                    {t('register.privacy_policy', 'Privacy Policy')}
                  </a>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50"
                disabled={loading || !tosAccepted}
                data-testid="register-submit"
              >
                {loading ? t('register.creating') : t('register.create_account')}
                {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </form>

            <OrDivider variant="light" />
            <GoogleSignInButton variant="light" label="Sign up with Google" testId="register-google-signin-btn" />

            <p className="text-center text-sm text-gray-500 mt-6">
              {t('register.already_have_account')}{' '}
              <Link to="/login" className="text-emerald-700 hover:underline font-medium" data-testid="login-link">
                {t('register.sign_in')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex flex-1 hero-gradient items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-3xl font-bold mb-6">{t('register.why_join')}</h2>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-slate-200">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Register;
